import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, X, Mail } from 'lucide-react';

import { UserAvatar } from '@/components/shared/UserAvatar';
import { RoleBadge } from '@/components/shared/RoleBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  groupService,
  type GroupMember,
  type WorkspaceUser,
} from '@/services/group.service';

interface GroupMembersDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  groupId: string;
  groupName: string;
  canManage: boolean;
  canChangeRoles: boolean;
}

export function GroupMembersDialog({
  open, onOpenChange, groupId, groupName, canManage, canChangeRoles,
}: GroupMembersDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [memberSearch, setMemberSearch] = useState('');
  const [inviteInput, setInviteInput] = useState('');
  const [suggestions, setSuggestions] = useState<WorkspaceUser[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const wsId = localStorage.getItem('active_workspace_id') || '';

  const { data, isLoading } = useQuery({
    queryKey: ['group-members', groupId],
    queryFn: () => groupService.listMembers(groupId, { limit: 50 }).then((r) => r.data),
    enabled: open,
  });

  const members: GroupMember[] = data?.data ?? [];
  const filtered = memberSearch
    ? members.filter((m) => m.user?.full_name?.toLowerCase().includes(memberSearch.toLowerCase()))
    : members;

  async function handleInviteInputChange(val: string) {
    setInviteInput(val);
    if (val.length >= 3 && wsId) {
      try {
        const res = await groupService.searchWorkspaceMembers(wsId, val);
        const existingIds = new Set(members.map((m) => m.user?.id));
        const wsMembers = (res.data.data || [])
          .map((m) => m.user)
          .filter((u) => u && !existingIds.has(u.id));
        setSuggestions(wsMembers as WorkspaceUser[]);
        setShowSuggestions(true);
      } catch { setSuggestions([]); }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }

  const addByUserMut = useMutation({
    mutationFn: (userId: string) => groupService.addMember(groupId, { user_id: userId, role: 'member' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-members', groupId] });
      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast({ title: 'Member added' });
      setInviteInput(''); setSuggestions([]); setShowSuggestions(false);
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed to add member', variant: 'destructive' }),
  });

  const addByEmailMut = useMutation({
    mutationFn: (email: string) => groupService.addMember(groupId, { email, role: 'member' }),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['group-members', groupId] });
      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      const msg = res.data?.message || 'Member added';
      toast({ title: msg.includes('Invitation') ? 'Invitation sent' : 'Member added' });
      setInviteInput('');
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  function handleInviteSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = inviteInput.trim();
    if (trimmed && trimmed.includes('@')) {
      addByEmailMut.mutate(trimmed);
    }
  }

  const removeMut = useMutation({
    mutationFn: (memberId: string) => groupService.removeMember(groupId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-members', groupId] });
      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast({ title: 'Member removed' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  const updateRoleMut = useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) => groupService.updateMember(groupId, memberId, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-members', groupId] });
      toast({ title: 'Role updated' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  const isValidEmail = inviteInput.includes('@') && inviteInput.trim().length > 3;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Members - {groupName}</DialogTitle>
          <DialogDescription>
            {canManage ? 'Add or remove members and manage their roles' : 'View group members'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {canManage && (
            <div className="border-2 border-primary/20 rounded-lg p-4 space-y-3 bg-primary/5">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                <h4 className="font-medium">Invite New Member</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Search by name (min 3 chars) to add existing users, or enter an email to invite new users.
              </p>
              <div className="relative">
                <form onSubmit={handleInviteSubmit} className="flex gap-2">
                  <Input
                    placeholder="Enter email address to invite..."
                    value={inviteInput}
                    onChange={(e) => handleInviteInputChange(e.target.value)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                    className="flex-1"
                  />
                  <Button type="submit" disabled={addByEmailMut.isPending || !isValidEmail}>
                    <Mail className="h-4 w-4 mr-2" /> Invite
                  </Button>
                </form>
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-16 z-50 mt-1 rounded-md border bg-popover shadow-md">
                    {suggestions.map((u) => (
                      <button
                        key={u.id}
                        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-accent transition-colors"
                        onMouseDown={(e) => { e.preventDefault(); addByUserMut.mutate(u.id); }}
                      >
                        <UserAvatar name={u.full_name} avatarUrl={u.avatar_url} size="xs" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{u.full_name}</p>
                          <p className="text-xs text-muted-foreground">Click to add to group</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <h4 className="font-medium text-sm">Current Members ({members.length})</h4>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Filter members..." value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} className="pl-8" />
            </div>
            {isLoading ? (
              <div className="space-y-2">{Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-14" />)}</div>
            ) : (
              filtered.map((member) => {
                const isOwner = member.role === 'owner';
                return (
                  <div key={member.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <UserAvatar name={member.user?.full_name ?? 'Unknown'} avatarUrl={member.user?.avatar_url} size="sm" />
                      <div>
                        <p className="font-medium">{member.user?.full_name}</p>
                        <p className="text-sm text-muted-foreground">{member.user?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {canChangeRoles && !isOwner ? (
                        <Select
                          value={member.role}
                          onValueChange={(v) => updateRoleMut.mutate({ memberId: member.id, role: v })}
                        >
                          <SelectTrigger className="w-28 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="member">Member</SelectItem>
                            <SelectItem value="moderator">Moderator</SelectItem>
                            <SelectItem value="owner">Owner</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <RoleBadge role={member.role} />
                      )}
                      {canManage && !isOwner && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeMut.mutate(member.id)}>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
